import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Container,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { BASE_URL } from "../../../config/apiConfig";
import { getHeaders } from "../../../utils/apiUtils";
import { useNavigate, useParams } from "react-router-dom";
import { showAlertWithMessage } from "../../../components/common/userFeedback/CustomAlert";

function OnboardingScreen() {
  const { id } = useParams();
  // State for form fields
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [userAccessLevel, setUserAccessLevel] = useState("");
  const [error, setError] = useState("");
  const [disabled, setDisabled] = useState(true);
  const [isActive, setIsActive] = useState(true);
  const [userAccessLevels, setUserAccessLevels] = useState([]);
  const navigate = useNavigate();

  const fetchUserGroups = async () => {
    let headers = await getHeaders();
    axios
      .get(`${BASE_URL}tools/admin/user-access-levels/list`, headers)
      .then((res) => {
        setUserAccessLevels(res.data.data);
      })
      .catch((err) => {
        setError(err.response?.data?.message);
        showAlertWithMessage(err.response?.data?.message, "error")
      });
  };

  const isValidEmail = (email) => {
    const regex = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/;
    return regex.test(email);
  };

  const fetchUserDetails = async (id) => {
    if (!id)
    {
      setName("");
      setEmail("");
      setUserAccessLevel("");
      setIsActive(true);
      return;
    }

    // TODO: Implement user creation logic, e.g., calling a backend API
    let headers = await getHeaders();

    axios
      .get(`${BASE_URL}tools/admin/users/get-details/${id}`, headers)
      .then((res) => {
        console.log(`res.data.data: ${JSON.stringify(res.data.data)}`)
        if (res.data.data)
        {
          // Reset form fields (optional)
          setName(res.data.data.bridge_user);
          setEmail(res.data.data.cognito.email);
          setUserAccessLevel(res.data.data.cognito.access_level);
          setIsActive(res.data.data.is_active);
        }
      })
      .catch((err) => {
        setError(err.response?.data?.message);
        showAlertWithMessage(err.response?.data?.message, "error")
      });
  }

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (disabled) return;

    // Construct user data object
    const userData = {
      userId: id,
      name,
      email,
      userAccessLevel,
      isActive: id ? isActive : true
    };

    // TODO: Implement user creation logic, e.g., calling a backend API
    let headers = await getHeaders();

    axios
      .post(`${BASE_URL}tools/admin/users/${id ? `update` : `create`}`, userData, headers)
      .then((res) => {
        // Reset form fields (optional)
        fetchUserDetails(id);
        showAlertWithMessage(res?.data?.message, "success")
        navigate("/admin/users")
      })
      .catch((err) => {
        setError(err.response?.data?.message);
        showAlertWithMessage(err.response?.data?.message, "error")
      });
  };

  useEffect(() => {
    if (!isValidEmail(email) || !name || !userAccessLevel) {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
  }, [email, name, userAccessLevel]);

  useEffect(() => {
    fetchUserGroups();
  }, []);

  useEffect(() => {
    fetchUserDetails(id);
  }, [id])

  return (
    <Container maxWidth="sm">
      <Box
        component="form"
        sx={{
          padding: 2,
          paddingTop: 2,
          display: "flex",
          flexDirection: "column",
          gap: 1, // Add some space between form elements
        }}
        autoComplete="off"
        onSubmit={handleSubmit}
      >
        <Typography variant="h2" sx={{mb: 1}}>{!id ? `New` : `Edit`} User</Typography>

        <TextField
          label="Name"
          id="name"
          variant="outlined"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />

        <TextField
          label="Email"
          variant="outlined"
          id="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          type="email"
          required
          disabled={id}
        />

        <Box sx={{flexDirection: "row", width: "100%", justifyContent: "space-between", display: "flex"}}>
          <FormControl fullWidth>
            <InputLabel id="user-access-level-label">User Access Level</InputLabel>
            <Select
              labelId="user-access-level-label" // Make sure this matches the id of the InputLabel
              id="user-access-level-select" // Adding an id here for the Select
              value={userAccessLevel}
              label="User Access Level" // This is important for MUI v5 for accessibility
              onChange={(e) => setUserAccessLevel(e.target.value)}
            >
              {userAccessLevels.map((accessLevel, index) => (
                <MenuItem key={accessLevel.label} value={accessLevel.value}>
                  {accessLevel.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {id && <FormControlLabel
            control={
              <Checkbox
                checked={isActive}
                onChange={() => setIsActive(!isActive)}
                color="primary"
              />
            }
            label={<Typography variant="body_lg">Active</Typography>}
            sx={{
              ml: 1,
              "& .MuiTypography-root": { fontSize: "1rem" },
            }}
          />}
        </Box>

        <Button type="submit" variant="angular" disabled={disabled}>
          {!id ? `Create` : `Update`} User
        </Button>
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      </Box>
    </Container>
  );
}

export default OnboardingScreen;
